import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  Shuffle,
  Repeat,
  Music,
  Upload,
  FolderOpen,
  Activity,
  Radio,
  Zap,
  Edit,
  Maximize2,
  Minimize2,
  X
} from 'lucide-react';
import { AudioVisualizer } from './AudioVisualizer';
import { cn } from '../../lib/utils';
import { FileSystemNavigator } from '../../utils/filesystem';
import { supabase } from '../../integrations/supabase/client';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/use-toast';
import { useUserVFS } from '../../hooks/useUserVFS';
import { parseBlob, type IAudioMetadata } from 'music-metadata';

interface Track {
  id: string;
  name: string;
  title?: string;
  artist?: string;
  path: string;
  url: string;
  duration?: number;
  file?: File;
  originalFileName?: string;
  artworkUrl?: string | null;
}

interface MusicPlayerProps {
  navigator?: FileSystemNavigator;
  initialTrackPath?: string;
}

export const MusicPlayer: React.FC<MusicPlayerProps> = ({ navigator, initialTrackPath }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState<Track | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(70);
  const [isMuted, setIsMuted] = useState(false);
  const [isShuffled, setIsShuffled] = useState(false);
  const [isRepeating, setIsRepeating] = useState(false);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [visualizerType, setVisualizerType] = useState<'bars' | 'wave' | 'spectrum' | 'retro'>('bars');
  const [showVisualizer, setShowVisualizer] = useState(true);
  const [visualizerKey, setVisualizerKey] = useState(0); // Force re-mount of visualizer

  // NEW: fullscreen visualizer state
  const [isFullscreenViz, setIsFullscreenViz] = useState(false);
  const [hudVisible, setHudVisible] = useState(true);
  const hudTimerRef = useRef<number | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const audioRef = useRef<HTMLAudioElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const artworkInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const { toast } = useToast();
  const { createFile, listFiles, fileExists, isLoaded: vfsLoaded } = useUserVFS();

  // Keep the audio element volume synchronized with UI state
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = (isMuted ? 0 : volume) / 100;
    }
  }, [isMuted, volume]);

  // Extract metadata from audio file using music-metadata (browser build via parseBlob)
  const extractMetadata = async (
    url: string,
    originalFileName: string
  ): Promise<{ title?: string; artist?: string }> => {
    try {
      const response = await fetch(url, { mode: 'cors' });
      const blob = await response.blob();
      const metadata: IAudioMetadata = await parseBlob(blob);

      console.log('Extracted metadata for', originalFileName, ':', metadata.common);

      const artist =
        metadata.common.artist ||
        metadata.common.albumartist ||
        metadata.common.artists?.[0] ||
        (metadata.common as any).contributing_artists;

      const result = {
        title: metadata.common.title,
        artist: artist
      };

      console.log('Final metadata result:', result);
      return result;
    } catch (error) {
      console.log('Could not extract metadata from:', originalFileName, error);
      return {};
    }
  };

  // Detect Last.fm placeholder images
  const isPlaceholderImage = (url: string): boolean => {
    if (!url || url.trim() === '') return true;
    const placeholderPatterns = [
      '2a96cbd8b46e442fc41c2b86b821562f',
      'c6f59c1e5e7240a4c0d427abd71f3dbb',
      'default_album',
      'placeholder',
      'noartist',
      'lastfm-placeholder'
    ];
    return placeholderPatterns.some((p) => url.includes(p));
  };

  // Fetch artist artwork from Supabase cache, otherwise try Last.fm
  const fetchArtistArtwork = async (artistName: string): Promise<string | null> => {
    try {
      const normalizedName = artistName.toLowerCase().trim();
      const { data: artist } = await supabase
        .from('os_artists')
        .select('artwork_url, artwork_source')
        .eq('normalized_name', normalizedName)
        .maybeSingle();

      if (artist && artist.artwork_url && !isPlaceholderImage(artist.artwork_url)) {
        console.log('✅ Found good cached artwork in Supabase for:', artistName, artist.artwork_url);
        return artist.artwork_url;
      }

      console.log('🔍 Trying fresh Last.fm lookup for:', artistName);

      const searchVariations = [
        artistName,
        'Mötley Crüe', // example with diacritics; replace as needed by your UI
        artistName.replace(/[^\w\s]/g, ''),
        artistName.replace(/[^\w\s]/g, ' ').replace(/\s+/g, ' ').trim()
      ];

      let fallbackPlaceholderUrl: string | null = null;

      for (const searchName of searchVariations) {
        try {
          const encodedArtist = encodeURIComponent(searchName);
          const response = await fetch(
            `https://ws.audioscrobbler.com/2.0/?method=artist.getinfo&artist=${encodedArtist}&api_key=4844d7b554873eebcd956eb8521c9c35&format=json&autocorrect=1`
          );

          if (!response.ok) continue;
          const data = await response.json();
          if (data.error) continue;

          const images = data.artist?.image;
          if (images && images.length > 0) {
            const largeImage =
              images.find((img: any) => img.size === 'extralarge') ||
              images.find((img: any) => img.size === 'large') ||
              images.find((img: any) => img.size === 'medium') ||
              images[images.length - 1];

            const imageUrl = largeImage?.['#text'];
            if (imageUrl && !isPlaceholderImage(imageUrl)) {
              if (user) {
                try {
                  await supabase.from('os_artists').insert({
                    name: artistName,
                    normalized_name: normalizedName,
                    artwork_url: imageUrl,
                    artwork_source: 'lastfm',
                    created_by: user.id
                  });
                } catch (_) {
                  // ignore unique constraint errors
                }
              }
              return imageUrl;
            } else if (imageUrl) {
              if (!fallbackPlaceholderUrl) fallbackPlaceholderUrl = imageUrl;
            }
          }
        } catch (e) {
          console.log(`Last.fm lookup failed for "${searchName}":`, e);
          continue;
        }
      }

      if (fallbackPlaceholderUrl) {
        if (user) {
          try {
            await supabase.from('os_artists').insert({
              name: artistName,
              normalized_name: normalizedName,
              artwork_url: fallbackPlaceholderUrl,
              artwork_source: 'lastfm_placeholder',
              created_by: user.id
            });
          } catch (_) {
            // ignore
          }
        }
        return fallbackPlaceholderUrl;
      }

      return null;
    } catch (error) {
      console.error('Error fetching artwork for artist:', artistName, error);
      return null;
    }
  };

  // Load audio files from Supabase storage and VFS
  // Load audio only after we have a user
  useEffect(() => {
    if (!user) return;
    loadAudioFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Add sample files only after VFS is ready and user is available
  useEffect(() => {
    if (!user || !vfsLoaded) return;
    addSampleAudioFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, vfsLoaded]);

  // Add sample audio files to VFS for testing
  const addSampleAudioFiles = async () => {
    if (!user) return;

  };

  const loadAudioFiles = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const supabaseTracks: Track[] = [];
      const vfsTracks: Track[] = [];

      // Prefer search (more robust), fall back to list
      const listAt = async (path: string) => {
        // Ensure directory form (e.g., "userId/") for listing
        const dir = path ? `${path.replace(/\/+$/, '')}/` : '';
        console.info('[MusicPlayer] Listing storage at:', dir || '(root)');
        const result = await supabase.storage.from('audio').list(dir, {
          limit: 1000,
          offset: 0,
          sortBy: { column: 'name', order: 'asc' },
        });
        if ((result as any).error) {
          console.warn('[MusicPlayer] Storage list error at', dir || '(root)', (result as any).error);
        }
        return result;
      };

      // Try user folder first
      const { data: userFiles, error: userErr } = await listAt(user.id);
      let files = userFiles;
      let listPrefix = userFiles && userFiles.length > 0 ? user.id : '';

      // Always also try root (combine if it has audio files)
      const { data: rootFiles, error: rootErr } = await listAt('');
      const rootHasFiles = Array.isArray(rootFiles) && rootFiles.length > 0;
      if ((!files || files.length === 0 || !Array.isArray(files)) && !userErr && rootHasFiles) {
        files = rootFiles;
        listPrefix = '';
      }

      if (files && files.length > 0) {
        const audioTracks: Track[] = await Promise.all(
          files
            .filter((file) => file.name.match(/\.(mp3|wav|ogg|m4a|aac|flac)$/i))
            .map(async (file) => {
              const filePath = listPrefix ? `${listPrefix}/${file.name}` : file.name;
              const { data: signedData, error: signedErr } = await supabase.storage
                .from('audio')
                .createSignedUrl(filePath, 60 * 60); // 1 hour
              const fallbackPublic = supabase.storage.from('audio').getPublicUrl(filePath).data.publicUrl;
              const fileUrl = signedErr ? fallbackPublic : (signedData?.signedUrl || fallbackPublic);

              const originalFileName = file.name.replace(/^\d+-/, '');

              let metadata: { title?: string; artist?: string } = {};
              if (/\.mp3$/i.test(file.name)) {
                metadata = await extractMetadata(fileUrl, originalFileName);
              }

              const displayName = metadata.title || originalFileName.replace(/\.(mp3|wav|ogg|m4a|aac|flac)$/i, '');

              let artworkUrl: string | null = null;
              if (metadata.artist) {
                artworkUrl = await fetchArtistArtwork(metadata.artist);
              }

              return {
                id: filePath,
                name: displayName,
                title: metadata.title,
                artist: metadata.artist,
                originalFileName,
                path: filePath,
                url: fileUrl,
                artworkUrl
              } as Track;
            })
        );
        supabaseTracks.push(...audioTracks);
      } else {
        console.info('No audio files found in Supabase storage under user folder or root.');
      }

      // VFS sample files
      const musicFiles = listFiles('C:\\User\\Music');
      for (const file of musicFiles) {
        if (file.file_name.match(/\.(wav|mp3|ogg|m4a|aac)$/i)) {
          let sampleUrl: string | null = null;
          if (sampleUrl) {
            vfsTracks.push({
              id: file.id,
              name: file.file_name.replace(/\.(wav|mp3|ogg|m4a|aac)$/i, ''),
              path: file.file_path,
              url: sampleUrl
            });
          }
        }
      }

      // Prefer Supabase tracks; include VFS demo only if no Supabase tracks were found.
      const baseList = supabaseTracks.length > 0 ? supabaseTracks : [...supabaseTracks, ...vfsTracks];

      // Functional update so we always merge with the latest state (avoids races after upload)
      setTracks((prev) => {
        const map = new Map<string, Track>();
        prev.forEach((t) => map.set(t.id, t));
        baseList.forEach((t) => map.set(t.id, t));
        const merged = Array.from(map.values());
        console.log('Loaded audio tracks (merged):', merged);
        return merged;
      });

      // REMOVED: auto-selecting first track here to avoid overriding a user-selected or newly uploaded track.

      if (tracks.length === 0) {
        toast({
          title: 'No audio files found',
          description: 'We checked your user folder and the bucket root. Try Upload Audio or click Refresh if you added files recently.',
        });
      }

    } catch (error) {
      console.error('Error loading audio files:', error);
      toast({
        title: 'Error',
        description: 'Failed to load audio files',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Keep selection stable when tracks refresh; only auto-select if none is selected.
  useEffect(() => {
    if (!currentTrack && tracks.length > 0) {
      setCurrentTrack(tracks[0]);
      setCurrentTrackIndex(0);
    } else if (currentTrack && tracks.length > 0) {
      const idx = tracks.findIndex((t) => t.id === currentTrack.id);
      if (idx !== -1 && idx !== currentTrackIndex) {
        setCurrentTrackIndex(idx);
      }
    }
  }, [tracks, currentTrack, currentTrackIndex]);

  // Upload custom artist artwork
  const handleArtworkUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user || !currentTrack?.artist) return;

    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid File', description: 'Please select an image file (PNG, JPG, etc.)', variant: 'destructive' });
      return;
    }

    setIsLoading(true);
    try {
      const artistName = currentTrack.artist;
      const normalizedName = artistName.toLowerCase().trim();
      const fileName = `${normalizedName.replace(/[^a-z0-9]/g, '_')}_${Date.now()}.${file.name.split('.').pop()}`;
      const filePath = fileName;

      const { error: uploadError } = await supabase.storage.from('artist-artwork').upload(filePath, file);
      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from('artist-artwork').getPublicUrl(filePath);

      const { error: upsertError } = await supabase
        .from('os_artists')
        .upsert(
          {
            name: artistName,
            normalized_name: normalizedName,
            artwork_url: data.publicUrl,
            artwork_source: 'user',
            created_by: user.id
          },
          { onConflict: 'normalized_name' }
        );
      if (upsertError) throw upsertError;

      toast({ title: 'Artwork Updated', description: `Custom artwork uploaded for ${artistName}` });

      await loadAudioFiles();
    } catch (error) {
      console.error('Error uploading artwork:', error);
      toast({ title: 'Upload Failed', description: 'There was an error uploading the artwork', variant: 'destructive' });
    } finally {
      setIsLoading(false);
      if (artworkInputRef.current) artworkInputRef.current.value = '';
    }
  };

  // Upload audio file to Supabase storage
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    if (!file.type.startsWith('audio/')) {
      toast({ title: 'Invalid File', description: 'Please select an audio file (MP3, WAV, OGG, etc.)', variant: 'destructive' });
      return;
    }

    setIsLoading(true);
    try {
      const fileName = `${Date.now()}-${file.name}`;
      const filePath = `${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage.from('audio').upload(filePath, file);
      if (uploadError) throw uploadError;

      // Fetch a signed URL for reliable playback even if the bucket is private
      const { data: signedData, error: signedErr } = await supabase.storage
        .from('audio')
        .createSignedUrl(filePath, 60 * 60); // 1 hour
      const fallbackPublic = supabase.storage.from('audio').getPublicUrl(filePath).data.publicUrl;
      const fileUrl = signedErr ? fallbackPublic : (signedData?.signedUrl || fallbackPublic);

      // Add newly uploaded track to the list immediately and start playing
      const uploadedTrack: Track = {
        id: filePath,
        name: file.name.replace(/\.(mp3|wav|ogg|m4a|aac)$/i, ''),
        path: filePath,
        url: fileUrl,
        originalFileName: file.name,
      };
      setTracks(prev => [uploadedTrack, ...prev]);
      setCurrentTrack(uploadedTrack);
      setCurrentTrackIndex(0);

      toast({ title: 'Upload Successful', description: `${file.name} has been uploaded to your music library` });
      // Try to start playback right away
      setTimeout(() => {
        if (audioRef.current) {
          void audioRef.current.play().then(() => setIsPlaying(true)).catch(console.error);
        }
      }, 150);
      // Also refresh library in the background (updates metadata/artwork)
      void loadAudioFiles();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({ title: 'Upload Failed', description: 'There was an error uploading your audio file', variant: 'destructive' });
    } finally {
      setIsLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Update audio element when track changes
  useEffect(() => {
    if (audioRef.current && currentTrack) {
      console.log('Loading track:', currentTrack.name, currentTrack.url);
      audioRef.current.crossOrigin = 'anonymous';
      audioRef.current.src = currentTrack.url;

      const handleLoadError = () => {
        console.error('Failed to load audio:', currentTrack.url);
        toast({
          title: 'Audio Load Error',
          description: `Failed to load ${currentTrack.name}. The file may be corrupted or in an unsupported format.`,
          variant: 'destructive'
        });
      };

      const handleCanPlay = () => {
        console.log('Audio can play:', currentTrack.name);
      };

      audioRef.current.addEventListener('error', handleLoadError);
      audioRef.current.addEventListener('canplay', handleCanPlay);
      audioRef.current.load();

      return () => {
        if (audioRef.current) {
          audioRef.current.removeEventListener('error', handleLoadError);
          audioRef.current.removeEventListener('canplay', handleCanPlay);
        }
      };
    }
  }, [currentTrack, toast]);

  // Update time and duration
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => {
      if (isRepeating) {
        audio.currentTime = 0;
        void audio.play();
      } else {
        void handleNext();
      }
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [isRepeating]);

  // NEW: fullscreen change sync (ESC or browser exits)
  useEffect(() => {
    const onFsChange = () => {
      const active = !!document.fullscreenElement;
      if (!active) {
        setIsFullscreenViz(false);
      }
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // NEW: request fullscreen when overlay mounts
  useEffect(() => {
    if (isFullscreenViz && overlayRef.current && overlayRef.current.requestFullscreen) {
      overlayRef.current.requestFullscreen().catch(() => {
        // If the request fails (e.g., non-user gesture), keep overlay without native fullscreen
      });
    }
  }, [isFullscreenViz]);

  // NEW: HUD auto-fade on inactivity
  const showHudTemporarily = () => {
    setHudVisible(true);
    if (hudTimerRef.current) {
      clearTimeout(hudTimerRef.current);
    }
    hudTimerRef.current = window.setTimeout(() => setHudVisible(false), 2000);
  };

  const handleOverlayMouseMove = () => {
    showHudTemporarily();
  };

  const enterFullscreenVisualizer = () => {
    setIsFullscreenViz(true);
    showHudTemporarily();
  };

  const exitFullscreenVisualizer = () => {
    setIsFullscreenViz(false);
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => {});
    }
    setHudVisible(true);
  };

  const togglePlay = async () => {
    if (!audioRef.current || !currentTrack) return;
    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        await audioRef.current.play();
        setIsPlaying(true);
      }
    } catch (error) {
      console.error('Error playing audio:', error);
      toast({ title: 'Playback Error', description: 'Unable to play this audio file', variant: 'destructive' });
    }
  };

  const handleNext = async () => {
    if (tracks.length === 0) return;
    let nextIndex: number;
    if (isShuffled) {
      nextIndex = Math.floor(Math.random() * tracks.length);
    } else {
      nextIndex = (currentTrackIndex + 1) % tracks.length;
    }
    setCurrentTrackIndex(nextIndex);
    setCurrentTrack(tracks[nextIndex]);
    if (isPlaying) {
      setTimeout(() => {
        if (audioRef.current) {
          void audioRef.current.play().catch(console.error);
        }
      }, 100);
    }
  };

  const handlePrevious = async () => {
    if (tracks.length === 0) return;
    let prevIndex: number;
    if (isShuffled) {
      prevIndex = Math.floor(Math.random() * tracks.length);
    } else {
      prevIndex = currentTrackIndex === 0 ? tracks.length - 1 : currentTrackIndex - 1;
    }
    setCurrentTrackIndex(prevIndex);
    setCurrentTrack(tracks[prevIndex]);
    if (isPlaying) {
      setTimeout(() => {
        if (audioRef.current) {
          void audioRef.current.play().catch(console.error);
        }
      }, 100);
    }
  };

  const handleSeek = (value: number[]) => {
    if (audioRef.current) {
      const newTime = (value[0] / 100) * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleVolumeChange = (value: number[]) => {
    const newVolume = value[0];
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume / 100;
    }
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.volume = volume / 100;
        setIsMuted(false);
      } else {
        audioRef.current.volume = 0;
        setIsMuted(true);
      }
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const selectTrack = async (track: Track, index: number) => {
    setCurrentTrack(track);
    setCurrentTrackIndex(index);
    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current
          .play()
          .then(() => setIsPlaying(true))
          .catch(console.error);
      }
    }, 100);
  };

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-primary/10 via-background to-accent/10">
      <audio
        ref={audioRef}
        onError={(e) => {
          console.error('Audio error:', e);
          toast({ title: 'Audio Error', description: 'Failed to load audio file', variant: 'destructive' });
        }}
      />

      {/* Hidden file inputs */}
      <input ref={fileInputRef} type="file" accept="audio/*" onChange={handleFileUpload} className="hidden" multiple={false} />
      <input ref={artworkInputRef} type="file" accept="image/*" onChange={handleArtworkUpload} className="hidden" multiple={false} />

      {/* Header */}
      <div className="p-6 border-b border-border/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-primary/20">
              <Music className="w-6 h-6 text-primary" />
            </div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Music Player</h1>
          </div>

          {/* Upload Controls */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={!user || isLoading}>
              <Upload className="w-4 h-4 mr-2" />
              Upload Audio
            </Button>
            <Button variant="outline" size="sm" onClick={loadAudioFiles} disabled={!user || isLoading}>
              <FolderOpen className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex">
        {/* Sidebar - Track List */}
        <div className="w-80 border-r border-border/50 bg-muted/30">
          <div className="p-4 border-b border-border/50">
            <h3 className="font-semibold text-muted-foreground">Playlist</h3>
          </div>
          <div className="p-2 space-y-1 max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center text-muted-foreground">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                <p className="text-sm">Loading music files...</p>
              </div>
            ) : tracks.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                <Music className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No music files found</p>
                <p className="text-xs mb-3">Upload audio files to get started</p>
                <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={!user}>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Audio
                </Button>
              </div>
            ) : (
              tracks.map((track, index) => (
                <div
                  key={track.id}
                  className={cn(
                    'p-3 rounded-lg cursor-pointer transition-all duration-200 hover:bg-accent/50',
                    currentTrack?.id === track.id && 'bg-primary/20 border border-primary/40'
                  )}
                  onClick={() => selectTrack(track, index)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                      <Music className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{track.title || track.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{track.artist || 'Unknown Artist'}</p>
                    </div>
                    {currentTrack?.id === track.id && isPlaying && <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Main Player */}
        <div className="flex-1 flex flex-col">
          {/* Visualizer Controls */}
          <div className="p-4 border-b border-border/50 bg-muted/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button
                  variant={showVisualizer ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setShowVisualizer(!showVisualizer);
                    if (!showVisualizer) setVisualizerKey((prev) => prev + 1);
                  }}
                >
                  <Activity className="w-4 h-4 mr-2" />
                  Visualizer
                </Button>

                {showVisualizer && (
                  <div className="flex items-center gap-1 ml-4">
                    <Button variant={visualizerType === 'bars' ? 'default' : 'outline'} size="sm" onClick={() => setVisualizerType('bars')}>
                      Bars
                    </Button>
                    <Button variant={visualizerType === 'wave' ? 'default' : 'outline'} size="sm" onClick={() => setVisualizerType('wave')}>
                      Wave
                    </Button>
                    <Button variant={visualizerType === 'spectrum' ? 'default' : 'outline'} size="sm" onClick={() => setVisualizerType('spectrum')}>
                      <Radio className="w-4 h-4 mr-1" />
                      Spectrum
                    </Button>
                    <Button variant={visualizerType === 'retro' ? 'default' : 'outline'} size="sm" onClick={() => setVisualizerType('retro')}>
                      <Zap className="w-4 h-4 mr-1" />
                      Retro
                    </Button>

                    {/* NEW: Fullscreen Visualizer button */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="ml-2"
                      onClick={enterFullscreenVisualizer}
                      disabled={!currentTrack}
                      aria-label="Fullscreen Visualizer"
                      title="Fullscreen Visualizer"
                    >
                      <Maximize2 className="w-4 h-4 mr-2" />
                      Fullscreen
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Now Playing */}
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="text-center max-w-md w-full">
              {showVisualizer ? (
                <div className="w-full h-48 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-background/50 to-muted/30 border border-border/50 overflow-hidden">
                  <AudioVisualizer
                    key={visualizerKey}
                    audioElement={audioRef.current}
                    isPlaying={isPlaying}
                    visualizerType={visualizerType}
                    className="w-full h-full"
                  />
                </div>
              ) : (
                <div className="relative w-48 h-48 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary/30 via-accent/20 to-primary/40 flex items-center justify-center shadow-2xl overflow-hidden group">
                  {currentTrack?.artworkUrl ? (
                    <>
                      <img
                        src={currentTrack.artworkUrl}
                        alt={`${currentTrack.artist || 'Artist'} artwork`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                      <Button
                        variant="secondary"
                        size="sm"
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => artworkInputRef.current?.click()}
                        disabled={!currentTrack?.artist}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="ghost"
                      className="w-full h-full flex flex-col items-center justify-center gap-2 text-white/80 hover:text-white"
                      onClick={() => artworkInputRef.current?.click()}
                      disabled={!currentTrack?.artist}
                    >
                      <Music className="w-24 h-24" />
                      <span className="text-sm">Add Artwork</span>
                    </Button>
                  )}
                  <Music className={cn('w-24 h-24 text-white/80', currentTrack?.artworkUrl && 'hidden')} />
                </div>
              )}

              <h2 className="text-2xl font-bold mb-2">{currentTrack ? currentTrack.title || currentTrack.name : 'No Track Selected'}</h2>
              <p className="text-muted-foreground mb-6">{currentTrack ? currentTrack.artist || 'Unknown Artist' : 'Select a track to start playing'}</p>

              {/* Progress Bar */}
              <div className="mb-6">
                <Slider value={[duration ? (currentTime / duration) * 100 : 0]} onValueChange={handleSeek} className="mb-2" disabled={!currentTrack} />
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center justify-center gap-4 mb-6">
                <Button variant="ghost" size="icon" className={cn('w-10 h-10', isShuffled && 'text-primary')} onClick={() => setIsShuffled(!isShuffled)}>
                  <Shuffle className="w-5 h-5" />
                </Button>

                <Button variant="ghost" size="icon" className="w-12 h-12" onClick={handlePrevious} disabled={tracks.length === 0}>
                  <SkipBack className="w-6 h-6" />
                </Button>

                <Button
                  size="icon"
                  className="w-16 h-16 rounded-full bg-gradient-to-r from-primary to-accent hover:shadow-lg transition-all duration-200"
                  onClick={togglePlay}
                  disabled={!currentTrack}
                >
                  {isPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-1" />}
                </Button>

                <Button variant="ghost" size="icon" className="w-12 h-12" onClick={handleNext} disabled={tracks.length === 0}>
                  <SkipForward className="w-6 h-6" />
                </Button>

                <Button variant="ghost" size="icon" className={cn('w-10 h-10', isRepeating && 'text-primary')} onClick={() => setIsRepeating(!isRepeating)}>
                  <Repeat className="w-5 h-5" />
                </Button>
              </div>

              {/* Volume Control */}
              <div className="flex items-center justify-center gap-3 max-w-48 mx-auto">
                <Button variant="ghost" size="icon" className="w-8 h-8" onClick={toggleMute}>
                  {isMuted || volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </Button>
                <Slider value={[isMuted ? 0 : volume]} onValueChange={handleVolumeChange} className="flex-1" />
              </div>
            </div>
          </div>
        </div>

        {/* NEW: Fullscreen visualizer overlay */}
        {isFullscreenViz && (
          <div
            ref={overlayRef}
            className="fixed inset-0 z-[9999] bg-gradient-to-b from-black via-neutral-950 to-black"
            onMouseMove={handleOverlayMouseMove}
          >
            {/* Visualizer fills the screen */}
            <AudioVisualizer
              key={`fs-${visualizerKey}`}
              audioElement={audioRef.current}
              isPlaying={isPlaying}
              visualizerType={visualizerType}
              className="absolute inset-0"
            />

            {/* HUD - auto-fading controls and info */}
            <div
              className={cn(
                "absolute inset-x-0 top-0 p-4 flex items-center justify-between transition-opacity duration-300",
                hudVisible ? "opacity-100" : "opacity-0"
              )}
            >
              <div className="flex items-center gap-3 text-white">
                <div className="px-2 py-1 rounded bg-white/10 backdrop-blur-sm border border-white/10 font-mono text-xs">
                  {visualizerType.toUpperCase()}
                </div>
                <div className="px-2 py-1 rounded bg-white/10 backdrop-blur-sm border border-white/10 text-sm max-w-[50vw] truncate">
                  {currentTrack ? `${currentTrack.title || currentTrack.name} — ${currentTrack.artist || 'Unknown Artist'}` : 'No Track'}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  className="bg-white/10 hover:bg-white/20 text-white border-white/20"
                  onClick={() => {
                    const order: Array<'bars' | 'wave' | 'spectrum' | 'retro'> = ['bars', 'wave', 'spectrum', 'retro'];
                    const i = order.indexOf(visualizerType);
                    setVisualizerType(order[(i + 1) % order.length]);
                  }}
                >
                  Cycle
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  className="bg-white/10 hover:bg-white/20 text-white border-white/20"
                  onClick={exitFullscreenVisualizer}
                >
                  <Minimize2 className="w-4 h-4 mr-2" />
                  Exit
                </Button>
              </div>
            </div>

            {/* Bottom time/volume bar */}
            <div
              className={cn(
                "absolute inset-x-0 bottom-0 p-6 transition-opacity duration-300",
                hudVisible ? "opacity-100" : "opacity-0"
              )}
            >
              <div className="mx-auto max-w-3xl">
                <Slider
                  value={[duration ? (currentTime / duration) * 100 : 0]}
                  onValueChange={handleSeek}
                  className="mb-3"
                />
                <div className="flex items-center justify-between text-xs text-white/80 font-mono">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>
            </div>

            {/* Center play/pause control (tap-friendly) */}
            <div className="absolute inset-0 flex items-center justify-center">
              <Button
                size="icon"
                className="w-16 h-16 rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-sm"
                onClick={togglePlay}
                aria-label={isPlaying ? 'Pause' : 'Play'}
                title={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-1" />}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};